import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { AttributeOptionActionEnum, AttributeSchemaFormType } from "../AttributeSchemaEditorUtils";
import FormPropertySection from "../../forms/form-property-section/FormPropertySection";
import { FormFieldPropsBase } from "../../../components/forms/form/Form";
import { Box, Button, Stack, useTheme } from "@mui/material";
import { DndContext, closestCenter, DragOverlay, DragEndEvent, DragStartEvent, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { restrictToVerticalAxis, restrictToParentElement } from "@dnd-kit/modifiers";
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { Ref, forwardRef, useImperativeHandle, useState } from "react";
import { AttributeSchemaTypeEnum, IAttributeValueOption } from "../../../services/attribute-data-service/Models";
import { AttributeValueOption } from "./AttributeValueOption";
import { BaseActions } from "../../../components/action-menu/ActionMenuItem";

export type AttributeValueOptionType = {
    executeAction: (action: AttributeOptionActionEnum | BaseActions) => void;
};


export type AttributeValueOptionsProps = FormFieldPropsBase & {
};

function AttributeValueOptions({
    readOnly,
}: AttributeValueOptionsProps, ref: Ref<unknown> | undefined) { 

    const { t } = useTranslation();
    const theme = useTheme();
    
    const [draggingItem, setDraggingItem] = useState<IAttributeValueOption | undefined>(undefined);            

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                delay: 0,
                tolerance: 5
            }
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );
   
    useImperativeHandle<unknown, AttributeValueOptionType>(
        ref,
        () => ({
            executeAction: executeAction
        })
    );

    const {
        //getValues,
        watch,
        trigger,
        control,
        //reset,
        //setValue,
        //resetField
    } = useFormContext<AttributeSchemaFormType>();    
    
    const {
        fields: valueOptions,
        append,//: appendAttributes, TODO when adding editing
        remove, //,removeAttributes
        move
    } = useFieldArray({
        control: control,
        name: "valueOptions",
        keyName: "fieldKey"
    });

    const schemaType = watch('schemaType');

    const attributeSchemaForm = useWatch({ control: control }) as AttributeSchemaFormType;

    function onAddValueOption() {

        const ids = valueOptions.map(o => o.id);
        const minID = ids.length > 0 ? Math.min(...ids) - 1 : -1
        const nextID = minID >= 0 ? -1 : minID;

        append({
            id: nextID,
            schemaID: attributeSchemaForm.id,
            value: '',
            order: ids.length
        } as IAttributeValueOption)

        setTimeout(() => {
            trigger(`valueOptions.${ids.length}.value`, {shouldFocus: true});
        }, 200);
    }

    function onDelete(id: number) {

        const itemIndex = valueOptions.findIndex(o => o.id === id);

        if (itemIndex !== -1) {
            remove(itemIndex)
        }
        

    }

    function executeAction(action: AttributeOptionActionEnum | BaseActions) {

        switch(action) {
            case AttributeOptionActionEnum.Add:
                onAddValueOption();
                break;
        }
    }


    function onDragStart(event: DragStartEvent) {
        const item = valueOptions.find(o => o.id.toString() === event.active.id);
        setDraggingItem(item);
    }

    function onDragEnd(event: DragEndEvent) {
        const { active, over } = event;

        if (active.id !== over?.id) {
            const oldIndex = valueOptions.findIndex(o => o.id === +active.id);
            const newIndex = valueOptions.findIndex(o => o.id === +(over?.id ?? '0'));

            if (oldIndex !== undefined && newIndex !== undefined) {

                move(oldIndex, newIndex);

            }
        }

        setDraggingItem(undefined);
    }

    return (
        <FormPropertySection label={

            <Stack
                direction='column'
                sx={{
                    top: theme.spacing(1),
                    right: theme.spacing(3)
                }}>
                
                <Box sx={{
                    fontWeight: 'normal'
                }}>
                    {t('attributeSchema.attributeOptions')}
                </Box>

                {(schemaType === AttributeSchemaTypeEnum.PredefinedValue || schemaType === AttributeSchemaTypeEnum.PredefinedValueAndFreeformText) ? 
                    <Button sx={{alignSelf: 'flex-start'}} onClick={() => onAddValueOption()}>
                        {t('attributeSchema.addOption')}
                    </Button>
                : 
                null 
                }

            </Stack>


        }>
            <Stack
                direction='column'
                sx={{
                    flex: '1 1 auto',
                    overflow: 'hidden',
                    overflowY: 'auto'
                }}>


                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragStart={onDragStart}
                    onDragEnd={onDragEnd}
                    modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                >

                    <SortableContext 
                        items={valueOptions.map(o => o.fieldKey)}
                        strategy={verticalListSortingStrategy} >

                        {valueOptions.map((o, index) =>

                            <AttributeValueOption 
                                key={o.fieldKey}
                                id={o.id} 
                                name={`valueOptions[${index}].value`} 
                                readOnly={readOnly} 
                                onDelete={onDelete}
                            />
                        )}

                        <DragOverlay>
                            {
                                draggingItem &&

                                <AttributeValueOption 
                                    id={99999999}
                                    name='dnditem' 
                                    readOnly={true} 
                                />
                            }
                        </DragOverlay>

                    </SortableContext>
              </DndContext>

            </Stack>
        </FormPropertySection>
    );
}


export default forwardRef(AttributeValueOptions);